import { createTheme } from '@mui/material/styles';

const brand = {
  ink: '#102A43',
  inkDark: '#081A2A',
  blue: '#245B88',
  teal: '#0D9488',
  amber: '#D97706',
  canvas: '#F5F8FB',
};

const getDesignTokens = (mode) => {
  const isLight = mode === 'light';
  const paper = isLight ? '#FFFFFF' : '#132536';
  const canvas = isLight ? brand.canvas : '#08131F';
  const border = isLight ? '#DFE7EF' : '#365268';

  return {
    palette: {
      mode,
      primary: {
        light: isLight ? '#4C7DA4' : '#A7CAE3',
        main: isLight ? brand.ink : '#78AFD5',
        dark: isLight ? brand.inkDark : '#4F88B1',
        contrastText: isLight ? '#FFFFFF' : '#071522',
      },
      secondary: {
        light: isLight ? '#36B8AC' : '#72DDD3',
        main: isLight ? brand.teal : '#3BC4B8',
        dark: isLight ? '#08766D' : '#20A99E',
        contrastText: isLight ? '#FFFFFF' : '#061C1A',
      },
      success: { main: isLight ? '#16855B' : '#4CCB93' },
      warning: { main: isLight ? brand.amber : '#F4B451' },
      error: { main: isLight ? '#C2414B' : '#F07078' },
      info: { main: isLight ? brand.blue : '#72A8D0' },
      background: { default: canvas, paper },
      divider: border,
      text: isLight
        ? { primary: '#142333', secondary: '#5C6F82', disabled: '#8C9BA9' }
        : { primary: '#F7FAFC', secondary: '#B9CAD8', disabled: '#8DA4B7' },
      grey: isLight
        ? {
            50: '#F7FAFC', 100: '#EDF2F7', 200: '#DFE7EF', 300: '#C7D3DE',
            400: '#8FA1B2', 500: '#64788A', 600: '#4A5E70', 700: '#34495C',
            800: '#1E3447', 900: '#102A43',
          }
        : {
            50: '#10202F', 100: '#1A3042', 200: '#284357', 300: '#3A586E',
            400: '#587389', 500: '#7890A3', 600: '#9BAEBD', 700: '#BBC9D4',
            800: '#D8E1E8', 900: '#F2F6F9',
          },
      action: isLight
        ? {}
        : {
            active: '#C8D7E2',
            hover: 'rgba(151, 190, 219, 0.10)',
            selected: 'rgba(59, 196, 184, 0.16)',
            disabled: '#7890A3',
            disabledBackground: 'rgba(120, 144, 163, 0.14)',
            focus: 'rgba(120, 175, 213, 0.18)',
          },
    },
    shape: { borderRadius: 14 },
    typography: {
      fontFamily: ['Inter', 'Avenir Next', '-apple-system', 'BlinkMacSystemFont', '"Segoe UI"', 'sans-serif'].join(','),
      h1: { fontSize: 'clamp(2.45rem, 6vw, 4.8rem)', fontWeight: 760, lineHeight: 1.02, letterSpacing: '-0.045em' },
      h2: { fontSize: 'clamp(2rem, 4vw, 3.25rem)', fontWeight: 740, lineHeight: 1.08, letterSpacing: '-0.035em' },
      h3: { fontSize: 'clamp(1.6rem, 3vw, 2.3rem)', fontWeight: 720, lineHeight: 1.15, letterSpacing: '-0.025em' },
      h4: { fontSize: '1.5rem', fontWeight: 720, lineHeight: 1.2, letterSpacing: '-0.018em' },
      h5: { fontSize: '1.18rem', fontWeight: 700, lineHeight: 1.3 },
      h6: { fontSize: '1rem', fontWeight: 700, lineHeight: 1.35 },
      subtitle1: { fontWeight: 650 },
      body1: { fontSize: '0.98rem', lineHeight: 1.62 },
      body2: { fontSize: '0.875rem', lineHeight: 1.52 },
      caption: { fontSize: '0.76rem', fontWeight: 560, lineHeight: 1.4 },
      button: { fontSize: '0.875rem', fontWeight: 700, textTransform: 'none', letterSpacing: '-0.005em' },
      overline: { fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.11em', lineHeight: 1.8 },
    },
    shadows: [
      'none', '0 1px 2px rgba(16, 42, 67, 0.04)', '0 4px 12px rgba(16, 42, 67, 0.06)',
      '0 8px 24px rgba(16, 42, 67, 0.08)', '0 12px 32px rgba(16, 42, 67, 0.10)',
      ...Array(20).fill('0 18px 48px rgba(16, 42, 67, 0.12)'),
    ],
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          html: { scrollBehavior: 'smooth', colorScheme: mode },
          body: { backgroundColor: canvas, color: isLight ? '#142333' : '#F7FAFC' },
          '::selection': { backgroundColor: 'rgba(13, 148, 136, 0.22)' },
          '*': { boxSizing: 'border-box' },
        },
      },
      MuiPaper: { defaultProps: { elevation: 0 }, styleOverrides: { root: { backgroundImage: 'none' } } },
      MuiCard: {
        styleOverrides: { root: { border: `1px solid ${border}`, boxShadow: isLight ? '0 4px 20px rgba(16, 42, 67, 0.045)' : '0 10px 28px rgba(0, 0, 0, 0.18)' } },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { minHeight: 40, borderRadius: 10, paddingInline: 18 },
          containedPrimary: {
            color: '#FFFFFF',
            backgroundColor: isLight ? brand.ink : '#2D6A96',
            boxShadow: isLight ? '0 8px 18px rgba(16, 42, 67, 0.18)' : '0 8px 20px rgba(0, 0, 0, 0.24)',
            '&:hover': { backgroundColor: isLight ? '#173F60' : '#3B7EAD', boxShadow: '0 10px 24px rgba(16, 42, 67, 0.24)' },
          },
          outlined: { borderColor: border, '&:hover': { borderColor: isLight ? brand.blue : '#78AFD5', backgroundColor: isLight ? 'rgba(36, 91, 136, 0.045)' : 'rgba(120, 175, 213, 0.10)' } },
        },
      },
      MuiIconButton: { styleOverrides: { root: { borderRadius: 10 } } },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: paper, borderRadius: 11,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: border },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#9CB0C2' },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderWidth: 1.5 },
          },
        },
      },
      MuiChip: { styleOverrides: { root: { borderRadius: 8, fontWeight: 650 } } },
      MuiTooltip: { styleOverrides: { tooltip: { borderRadius: 8, fontSize: '0.75rem', padding: '7px 10px' } } },
      MuiDialog: { styleOverrides: { paper: { borderRadius: 20, border: `1px solid ${border}` } } },
    },
  };
};

export const getTheme = (mode = 'light') => createTheme(getDesignTokens(mode));
export default getTheme('light');
